import React, { RefObject, useEffect } from 'react';
import styled from 'styled-components';
import Link from 'components/core/link';

import {
  PopoverNavButton,
  PopoverNavItem,
  PopoverNavLink,
  StyledPopover,
  buttonResetStyles,
} from 'src/dispensary/core-menu/components/header/header.styles';

import { EmbeddedAccountIcon } from 'src/assets/embedded-account-icon';
import { useUserButton } from './use-user-button';

type UserButtonProps = {
  onRef?: (ref: React.RefObject<HTMLButtonElement>) => void;
  onLogin?: () => void;
  icon?: React.ReactNode;
  className?: string;
  customLabel?: React.ReactNode;
};

export const UserButton = ({
  onRef,
  onLogin,
  icon = <EmbeddedAccountIcon fill='currentColor' />,
  className,
  customLabel,
}: UserButtonProps = {}): JSX.Element | null => {
  const {
    isUserLoggedIn,
    firstName,
    isPopoverOpen,
    popoverTriggerRef,
    setIsPopoverOpen,
    loggedInRoutes,
    loyaltyPoints,
    handleLogout,
    handleLogin,
    handlePopoverLinkClick,
    handlePopoverToggle,
  } = useUserButton({ onLogin });

  const hasLoyaltyPoints = Number.isInteger(Number.parseInt(loyaltyPoints, 10));

  const defaultLabel = isUserLoggedIn ? (
    <div>
      <FirstName>{firstName}</FirstName> {hasLoyaltyPoints ? `${loyaltyPoints} pts` : 'Account'}
    </div>
  ) : (
    'Login'
  );

  useEffect(() => {
    if (onRef) {
      onRef(popoverTriggerRef);
    }
  }, [onRef, popoverTriggerRef]);

  return (
    <>
      <Button
        className={className}
        onClick={isUserLoggedIn ? () => handlePopoverToggle('Account Button') : handleLogin}
        ref={popoverTriggerRef}
        data-testid='user-button'
      >
        {icon}
        {customLabel ?? defaultLabel}
      </Button>

      {isUserLoggedIn && (
        <StyledPopover
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorEl={popoverTriggerRef.current}
          open={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          data-testid='user-button-popover'
          $offsetTop
        >
          <nav>
            <>
              <PopoverFirstName>Hi, {firstName}</PopoverFirstName>
              {loggedInRoutes.map(
                ({ href, label, trackingLabel, hide }) =>
                  !hide && (
                    <Link legacyBehavior href={href} key={trackingLabel ?? label}>
                      <PopoverNavLink href={href} onClick={() => handlePopoverLinkClick(trackingLabel ?? label)}>
                        {label}
                      </PopoverNavLink>
                    </Link>
                  )
              )}
              <PopoverNavButton onClick={handleLogout}>Logout</PopoverNavButton>
            </>
          </nav>
        </StyledPopover>
      )}
    </>
  );
};

const Button = styled.button`
  ${buttonResetStyles}

  align-items: center;
  color: var(--dcm-user-btn-color);
  display: flex;
  font-size: var(--dcm-user-btn-font-size);
  font-weight: var(--dcm-user-btn-font-weight);
  gap: var(--dcm-user-btn-gap);
  justify-content: space-between;
  line-height: ${18 / 13};

  @media (min-width: 880px) {
    gap: var(--dcm-user-btn-gap-desktop);
  }
`;

const PopoverFirstName = styled(PopoverNavItem)`
  border-bottom: 1px solid var(--dcm-user-btn-name-border-color);
  margin: 0 24px;
  padding-left: 0;
  padding-right: 0;
  width: auto;

  @media (min-width: 880px) {
    display: none;
  }
`;

const FirstName = styled.div`
  color: var(--dcm-user-btn-name-color);
  font-family: var(--dcm-font-secondary);
  max-width: var(--dcm-user-btn-name-max-width);
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 879px) {
    display: none;
  }
`;
